"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, Delete, Loader2 } from "lucide-react";
import Link from "@/components/i18n/link";
import { ApiError } from "@/lib/api-client";
import { reportError } from "@/lib/client-log";
import { useCountry } from "@/lib/country-store";
import {
  claimDailyWordGame,
  clearDailyWordGame,
  draftComplete,
  emptyDraft,
  eraseInDraft,
  fetchDailyWordToday,
  fetchMyDailyWordToday,
  KEYBOARD_ROWS,
  keyStates,
  loadAnyDailyWordGame,
  loadDailyWordGame,
  resultGrid,
  saveDailyWordGame,
  sendDailyWordGuess,
  sendMyDailyWordGuess,
  startDailyWord,
  startMyDailyWord,
  toGameLetter,
  typeInDraft,
  type DailyWordGame as Game,
  type DailyWordStats,
  type DailyWordToday,
  type EventLink,
  type LetterResult,
  type WordDraft,
} from "@/lib/daily-word";
import { useAuthStore } from "@/lib/store/auth-store";
import { useCountdown } from "@/lib/use-countdown";
import { LOCALE_META, type Locale } from "@/lib/i18n/config";
import { useLocale, useMessages } from "@/lib/i18n/client";
import { localizePath } from "@/lib/i18n/paths";
import { track } from "@/lib/tracking/track";
import { cn } from "@/lib/utils";
import { JoinRankingCard } from "@/components/games/JoinRankingCard";
import messages from "./page.i18n";
import rankingMessages from "@/components/games/ranking.i18n";

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? "https://ciudad-viva.cl";
const DAILY_WORD_PATH = "/juegos/palabra-del-dia";

const CELL_COLORS: Record<LetterResult, string> = {
  correct: "border-emerald-600 bg-emerald-600 text-white",
  present: "border-amber-500 bg-amber-500 text-white",
  absent: "border-zinc-500 bg-zinc-500 text-white dark:border-zinc-600 dark:bg-zinc-600",
};

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

function newGame(puzzle: number): Game {
  return { puzzle, guesses: [], results: [], status: "playing" };
}

// La partida de hoy: de la cuenta con sesión; sin sesión, de este navegador.
async function loadGameState(
  lang: Locale,
  country: string,
  loggedIn: boolean,
): Promise<{
  today: DailyWordToday;
  game: Game;
  stats: DailyWordStats | null;
  imported: boolean;
  // Hoy ya jugó en otro idioma (una partida al día).
  playedIn?: Locale;
}> {
  if (!loggedIn) {
    const today = await fetchDailyWordToday(lang, country);
    const local = loadDailyWordGame(lang, today.puzzle);
    return {
      today,
      game: local.game ?? newGame(today.puzzle),
      stats: null,
      imported: false,
      playedIn: local.playedIn,
    };
  }
  // La partida jugada sin sesión en este navegador pasa a la cuenta (si la
  // cuenta ya tiene la de ese día, el servidor la ignora).
  let imported = false;
  const local = loadAnyDailyWordGame();
  if (local?.token) {
    try {
      // En el idioma en que se jugó (el comprobante es de ese idioma).
      imported = (await claimDailyWordGame({ lang: local.lang ?? lang, country, token: local.token })).imported;
      clearDailyWordGame();
    } catch (err) {
      // 400: comprobante inválido o viejo → no hay nada que importar. Otro
      // error: se reintenta la próxima vez que cargue.
      if (err instanceof ApiError && err.status === 400) clearDailyWordGame();
      else reportError("daily-word-claim", err, { httpStatus: err instanceof ApiError ? err.status : undefined });
    }
  }
  const data = await fetchMyDailyWordToday(lang, country);
  return {
    today: data,
    game: data.game ?? newGame(data.puzzle),
    stats: data.stats,
    imported,
    playedIn: data.playedIn,
  };
}

export function DailyWordGame() {
  const t = useMessages(messages);
  const lang = useLocale();
  const { country, loaded } = useCountry();
  // Con sesión, la partida y las estadísticas viven en la cuenta; sin
  // sesión, en este navegador.
  const loggedIn = useAuthStore((s) => !!s.user);
  const authChecked = useAuthStore((s) => s.checked);
  const [today, setToday] = useState<DailyWordToday | null>(null);
  const [game, setGame] = useState<Game | null>(null);
  const [stats, setStats] = useState<DailyWordStats | null>(null);
  const [draft, setDraft] = useState<WordDraft>(() => emptyDraft(0));
  const [loadFailed, setLoadFailed] = useState(false);
  const [playedIn, setPlayedIn] = useState<Locale | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [copied, setCopied] = useState(false);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Partida cuyo reloj ya se mandó a partir (una vez por partida).
  const startedPuzzle = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const showNotice = useCallback((text: string) => {
    setNotice(text);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 2500);
  }, []);

  const loadToday = useCallback(() => {
    loadGameState(lang, country, loggedIn)
      .then((state) => {
        setToday(state.today);
        setGame(state.game);
        setStats(state.stats);
        setPlayedIn(state.playedIn);
        setLoadFailed(false);
        setDraft(emptyDraft(state.today.length));
        if (state.imported) showNotice(t.imported);
      })
      .catch((err: unknown) => {
        reportError("daily-word-today", err, { httpStatus: err instanceof ApiError ? err.status : undefined });
        setLoadFailed(true);
      });
  }, [lang, country, loggedIn, showNotice, t]);

  useEffect(() => {
    // Recién con el país y la sesión resueltos (iniciar o cerrar sesión
    // vuelve a cargar la partida que corresponde).
    if (loaded && authChecked) loadToday();
  }, [loaded, authChecked, loadToday]);

  const submit = useCallback(async () => {
    if (!today || !game || game.status !== "playing" || sending) return;
    if (!draftComplete(draft)) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      showNotice(t.notEnoughLetters);
      return;
    }
    setSending(true);
    const attempt = game.guesses.length + 1;
    const guess = draft.letters.join("");
    try {
      const body = { lang, country, puzzle: game.puzzle, guess };
      const res = loggedIn
        ? await sendMyDailyWordGuess(body)
        : await sendDailyWordGuess({ ...body, token: game.token });
      const finished = res.solved || attempt >= today.maxAttempts;
      const next: Game = {
        ...game,
        guesses: [...game.guesses, guess],
        results: [...game.results, res.result],
        status: res.solved ? "won" : finished ? "lost" : "playing",
        ...(res.answer && { answer: res.answer }),
        ...(res.events && { events: res.events }),
        ...(res.token && { token: res.token }),
      };
      setGame(next);
      if (loggedIn) {
        if (res.stats) setStats(res.stats);
      } else {
        saveDailyWordGame(lang, next);
      }
      setDraft(emptyDraft(today.length));
      if (finished) track("daily_word_finish", { puzzle: game.puzzle, solved: res.solved, attempts: attempt });
    } catch (err) {
      // Ya jugó hoy en otro idioma (en otra pestaña): se muestra eso.
      if (err instanceof ApiError && err.code === "PLAYED_OTHER_LANGUAGE") {
        loadToday();
        return;
      }
      // No es una palabra real: no gasta el intento, solo se avisa.
      if (err instanceof ApiError && err.code === "NOT_IN_DICTIONARY") {
        setShake(true);
        setTimeout(() => setShake(false), 400);
        showNotice(t.notInDictionary);
        return;
      }
      // 400: la partida ya no está disponible (pasaron dos medianoches con la
      // página abierta) o ya terminó; 409: se jugó en otra pestaña → se
      // vuelve a cargar la que corresponde.
      if (err instanceof ApiError && (err.status === 400 || err.status === 409)) {
        // Sin sesión, la partida guardada ya no sirve (vieja o con el
        // comprobante dañado): se descarta para no quedar trabada en ella.
        if (!loggedIn && err.status === 400) clearDailyWordGame();
        showNotice(err.status === 409 ? t.synced : t.expired);
        loadToday();
      } else {
        reportError("daily-word-guess", err, { httpStatus: err instanceof ApiError ? err.status : undefined });
        showNotice(t.guessError);
      }
    } finally {
      setSending(false);
    }
  }, [today, game, sending, draft, lang, country, loggedIn, showNotice, loadToday, t]);

  // Ranking por tiempo: el reloj parte con la primera letra de la partida.
  // Sin sesión, la hora de inicio viaja en el comprobante firmado. Si falla,
  // el servidor toma como inicio el primer intento.
  const startClock = useCallback(
    (current: Game) => {
      if (current.guesses.length > 0 || current.token || startedPuzzle.current === current.puzzle) return;
      startedPuzzle.current = current.puzzle;
      const body = { lang, country, puzzle: current.puzzle };
      if (loggedIn) {
        startMyDailyWord(body).catch((err: unknown) =>
          reportError("daily-word-start", err, { httpStatus: err instanceof ApiError ? err.status : undefined }),
        );
        return;
      }
      startDailyWord(body)
        .then(({ token }) =>
          // Solo si todavía no se mandó ningún intento (si no, ya hay otro).
          setGame((g) => {
            if (!g || g.puzzle !== current.puzzle || g.guesses.length > 0 || g.token) return g;
            const next = { ...g, token };
            saveDailyWordGame(lang, next);
            return next;
          }),
        )
        .catch((err: unknown) =>
          reportError("daily-word-start", err, { httpStatus: err instanceof ApiError ? err.status : undefined }),
        );
    },
    [lang, country, loggedIn],
  );

  const press = useCallback(
    (key: string) => {
      if (!today || game?.status !== "playing" || sending) return;
      if (key !== "ENTER" && key !== "BACKSPACE") startClock(game);
      if (key === "ENTER") {
        void submit();
      } else if (key === "BACKSPACE") {
        setDraft(eraseInDraft);
      } else {
        setDraft((d) => typeInDraft(d, key));
      }
    },
    [today, game, sending, submit, startClock],
  );

  const selectCell = useCallback((index: number) => {
    setDraft((d) => ({ ...d, cursor: index }));
  }, []);

  // Teclado físico.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest("input, textarea, select, [contenteditable]")) return;
      // Con un modal abierto (ej. el de organizadores) Radix oculta el resto
      // de la página con aria-hidden: las teclas no son para el juego.
      if (rootRef.current?.closest('[aria-hidden="true"]')) return;
      if (e.key === "Enter") {
        e.preventDefault();
        press("ENTER");
      } else if (e.key === "Backspace") {
        press("BACKSPACE");
      } else {
        const letter = toGameLetter(e.key, lang);
        if (letter) press(letter);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [press, lang]);

  if (loadFailed) {
    return (
      <div className="mt-8 flex flex-col items-center gap-3 text-center">
        <p className="text-sm text-muted-foreground">{t.loadError}</p>
        <button
          type="button"
          onClick={loadToday}
          className="inline-flex h-10 items-center justify-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          {t.retry}
        </button>
      </div>
    );
  }

  if (!today || !game) {
    return (
      <p className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> {t.loading}
      </p>
    );
  }

  if (playedIn) return <PlayedOtherLanguage playedIn={playedIn} t={t} />;

  const states = keyStates(game);
  const rows = Array.from({ length: today.maxAttempts }, (_, row) => row);
  // De 5 a 8 letras: las casillas se achican para que el tablero quepa en un
  // celular (en pantallas anchas, 56 px como máximo).
  const boardWidth = (cell: number) => `${today.length * cell + (today.length - 1) * 0.375}rem`;
  const activeRow = game.status === "playing" ? game.guesses.length : -1;

  return (
    <div ref={rootRef} className="mt-2 flex w-full max-w-md flex-col items-center">
      <p className="text-center text-sm text-muted-foreground">{t.intro(today.maxAttempts)}</p>
      {stats && stats.currentStreak > 0 && (
        <p className="mt-1 text-sm font-semibold">{t.streak(stats.currentStreak)}</p>
      )}

      <div
        role="grid"
        aria-label={t.board}
        className="mt-5 grid w-[min(100%,var(--board))] gap-1.5 sm:w-[min(100%,var(--board-sm))]"
        style={{ "--board": boardWidth(3), "--board-sm": boardWidth(3.5) } as React.CSSProperties}
      >
        {rows.map((row) => {
          const active = row === activeRow;
          const letters = row < game.guesses.length ? [...game.guesses[row]] : active ? draft.letters : [];
          const result = game.results[row];
          return (
            <div
              key={row}
              role="row"
              className={cn("grid gap-1.5", active && shake && "animate-shake")}
              style={{ gridTemplateColumns: `repeat(${today.length}, minmax(0, 1fr))` }}
            >
              {Array.from({ length: today.length }, (_, i) => {
                const letter = letters[i];
                const state = result?.[i];
                const label = letter ? `${letter}, ${state ? t.states[state] : ""}` : t.states.empty;
                const cellClass = cn(
                  "flex aspect-square w-full items-center justify-center rounded-md border-2 font-bold uppercase",
                  today.length > 6 ? "text-xl sm:text-2xl" : "text-2xl",
                  state
                    ? CELL_COLORS[state]
                    : active && i === draft.cursor
                      ? "border-primary bg-muted"
                      : letter
                        ? "border-foreground/60"
                        : "border-border",
                );
                // En la fila que se escribe, cada casilla se puede tocar para
                // elegir dónde va la siguiente letra.
                return active ? (
                  <div key={i} role="gridcell">
                    <button
                      type="button"
                      onClick={() => selectCell(i)}
                      aria-label={`${label}. ${t.selectCell}`}
                      className={cn(cellClass, "cursor-pointer")}
                    >
                      {letter ?? ""}
                    </button>
                  </div>
                ) : (
                  <div key={i} role="gridcell" aria-label={label} className={cellClass}>
                    {letter ?? ""}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <p aria-live="polite" className="mt-3 h-5 text-sm font-medium">
        {notice}
      </p>

      {game.status === "playing" ? (
        <>
          <Keyboard lang={lang} states={states} disabled={sending} onPress={press} t={t} />
          <Legend t={t} />
        </>
      ) : (
        <Finished
          game={game}
          today={today}
          stats={stats}
          onNextWord={loadToday}
          copied={copied}
          onCopied={() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        />
      )}
    </div>
  );
}

type T = (typeof messages)["es"];

function Keyboard({
  lang,
  states,
  disabled,
  onPress,
  t,
}: {
  lang: "es" | "en";
  states: Map<string, LetterResult>;
  disabled: boolean;
  onPress: (key: string) => void;
  t: T;
}) {
  return (
    <div aria-label={t.keyboard} className="mt-2 flex w-full flex-col gap-1.5">
      {KEYBOARD_ROWS[lang].map((row, r) => (
        <div key={r} className="flex justify-center gap-1">
          {row.map((key) => {
            const state = states.get(key);
            const wide = key === "ENTER" || key === "BACKSPACE";
            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                onClick={() => onPress(key)}
                aria-label={key === "ENTER" ? t.enter : key === "BACKSPACE" ? t.backspace : key}
                className={cn(
                  "flex h-12 min-w-0 items-center justify-center rounded-md text-sm font-semibold transition-colors disabled:opacity-60 sm:h-14",
                  wide ? "flex-[1.5] px-1 text-xs" : "flex-1",
                  state ? CELL_COLORS[state] : "bg-muted hover:bg-muted/70",
                )}
              >
                {key === "BACKSPACE" ? <Delete className="h-5 w-5" /> : key === "ENTER" ? t.enter : key}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function Legend({ t }: { t: T }) {
  return (
    <div className="mt-5 flex flex-col items-center gap-2 text-xs text-muted-foreground">
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
        {(["correct", "present", "absent"] as const).map((state) => (
          <span key={state} className="inline-flex items-center gap-1.5">
            <span className={cn("h-3.5 w-3.5 rounded-sm border", CELL_COLORS[state])} />
            {t.legend[state]}
          </span>
        ))}
      </div>
      <p>{t.accentsNote}</p>
    </div>
  );
}

function Finished({
  game,
  today,
  stats,
  onNextWord,
  copied,
  onCopied,
}: {
  game: Game;
  today: DailyWordToday;
  // null sin sesión: se invita a iniciarla para guardar la racha.
  stats: DailyWordStats | null;
  onNextWord: () => void;
  copied: boolean;
  onCopied: () => void;
}) {
  const t = useMessages(messages);
  const tRanking = useMessages(rankingMessages);
  const lang = useLocale();
  const countdown = useCountdown(today.nextChangeAt, onNextWord).text;
  const score = game.status === "won" ? String(game.guesses.length) : "X";
  const url = `${SITE_URL}${localizePath(DAILY_WORD_PATH, lang)}`;
  const shareText = `${t.shareTitle(game.puzzle)} ${score}/${today.maxAttempts}\n\n${resultGrid(game.results)}\n\n${url}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(shareText);
      track("share", { method: "copy_link", content_type: "daily_word" });
      onCopied();
    } catch (err) {
      reportError("daily-word-copy", err);
    }
  }

  return (
    <div className="mt-2 flex w-full flex-col items-center gap-4 rounded-2xl border border-border bg-card p-5 text-center shadow-sm">
      <div>
        {game.status === "won" ? (
          <>
            <p className="font-semibold">{t.won(game.guesses.length)}</p>
            {game.answer && (
              <p className="mt-1 text-sm text-muted-foreground">
                {t.todayWord} <span className="font-bold text-foreground">{game.answer}</span>
              </p>
            )}
            {game.events && game.events.length > 0 && <WordEvents events={game.events} lang={lang} t={t} />}
          </>
        ) : (
          // Al perder no se muestra la palabra (el servidor no la entrega):
          // un mensaje para volver mañana, el mismo para toda la partida.
          <p className="font-semibold">{t.lost[mod(game.puzzle, t.lost.length)]}</p>
        )}
      </div>
      {/* Vista previa de lo que se comparte, con los colores del tablero (los
          emoji ⬜/🟨 cambian de color según la fuente del sistema). */}
      <div aria-hidden className="flex flex-col gap-1">
        {game.results.map((row, r) => (
          <div key={r} className="flex gap-1">
            {row.map((state, i) => (
              <span key={i} className={cn("h-5 w-5 rounded-[3px] border", CELL_COLORS[state])} />
            ))}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        <a
          href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("share", { method: "whatsapp", content_type: "daily_word" })}
          className="inline-flex h-10 items-center justify-center rounded-full bg-[#25D366] px-5 text-sm font-semibold text-white hover:bg-[#1ebe5b]"
        >
          {t.shareWhatsApp}
        </a>
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-border px-5 text-sm font-semibold hover:bg-muted"
        >
          {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
          {copied ? t.copied : t.copyResult}
        </button>
      </div>
      <p className="text-sm text-muted-foreground">
        {t.nextWord} <span className="font-mono font-semibold text-foreground">{countdown}</span>
      </p>
      {stats ? (
        <>
          <JoinRankingCard />
          <Stats stats={stats} lastWin={game.status === "won" ? game.guesses.length : null} t={t} />
        </>
      ) : (
        <SaveStreakCta t={t} />
      )}
      <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
        <Link href={`${DAILY_WORD_PATH}/ranking`} className="text-sm font-semibold text-primary hover:underline">
          {tRanking.viewRanking}
        </Link>
        <Link href="/eventos" className="text-sm font-semibold text-primary hover:underline">
          {t.seeEvents}
        </Link>
        <Link href="/juegos/sopa-de-letras" className="text-sm font-semibold text-primary hover:underline">
          {t.playWordSearch}
        </Link>
      </div>
    </div>
  );
}

// Hoy ya jugó en otro idioma: una palabra al día, en cualquier idioma.
function PlayedOtherLanguage({ playedIn, t }: { playedIn: Locale; t: T }) {
  const language = LOCALE_META[playedIn].label;
  return (
    <div className="mt-4 flex w-full max-w-md flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
      <p className="font-semibold">{t.playedInTitle(language)}</p>
      <p className="text-sm text-muted-foreground">{t.playedInBody}</p>
      <a
        href={localizePath(DAILY_WORD_PATH, playedIn)}
        className="inline-flex h-10 items-center justify-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
      >
        {t.playedInLink(language)}
      </a>
    </div>
  );
}

// Eventos donde aparece la palabra de hoy (solo al acertar: sus títulos la
// delatan).
function WordEvents({ events, lang, t }: { events: EventLink[]; lang: Locale; t: T }) {
  return (
    <div className="mt-4 w-full text-left">
      <p className="text-center text-sm font-semibold">{t.wordEvents}</p>
      <ul className="mt-2 flex flex-col gap-2">
        {events.map((e) => (
          <li key={e.slug}>
            <Link
              href={`/evento/${e.slug}`}
              onClick={() => track("event_card_click", { slug: e.slug, list: "daily_word" })}
              className="flex items-center gap-3 rounded-xl border border-border p-2 transition hover:border-foreground/30"
            >
              {e.imageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element -- miniatura remota chica
                <img src={e.imageUrl} alt="" width={48} height={48} className="h-12 w-12 shrink-0 rounded-lg object-cover" />
              ) : (
                <span className="h-12 w-12 shrink-0 rounded-lg bg-muted" />
              )}
              <span className="min-w-0">
                <span className="line-clamp-2 text-sm font-medium">{e.title}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {new Date(e.date).toLocaleDateString(LOCALE_META[lang].intl, {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    timeZone: "UTC",
                  })}
                  {e.place && ` · ${e.place}`}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stats({ stats, lastWin, t }: { stats: DailyWordStats; lastWin: number | null; t: T }) {
  const max = Math.max(1, ...stats.distribution);
  const tiles = [
    [stats.played, t.stats.played],
    [stats.winRate, t.stats.winRate],
    [stats.currentStreak, t.stats.currentStreak],
    [stats.maxStreak, t.stats.maxStreak],
  ] as const;
  return (
    <div className="w-full border-t border-border pt-4">
      <p className="text-sm font-semibold">{t.stats.title}</p>
      <dl className="mt-3 grid grid-cols-4 gap-2">
        {tiles.map(([value, label]) => (
          <div key={label} className="flex flex-col items-center">
            <dd className="text-2xl font-bold tabular-nums">{value}</dd>
            <dt className="text-[11px] leading-tight text-muted-foreground">{label}</dt>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-xs font-medium text-muted-foreground">{t.stats.distribution}</p>
      <div className="mt-2 flex flex-col gap-1">
        {stats.distribution.map((count, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-3 text-right tabular-nums">{i + 1}</span>
            <div className="flex-1">
              <div
                className={cn(
                  "flex h-5 min-w-6 items-center justify-end rounded-sm px-1.5 font-semibold tabular-nums",
                  lastWin === i + 1 ? "bg-emerald-600 text-white" : "bg-muted text-foreground",
                )}
                style={{ width: `${Math.max(8, (count / max) * 100)}%` }}
              >
                {count}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SaveStreakCta({ t }: { t: T }) {
  return (
    <div className="w-full rounded-xl bg-primary/10 p-4">
      <p className="text-sm font-semibold">{t.saveStreak.title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{t.saveStreak.body}</p>
      <Link
        href={`/login?redirect=${encodeURIComponent(DAILY_WORD_PATH)}`}
        className="mt-3 inline-flex h-10 items-center justify-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
      >
        {t.saveStreak.button}
      </Link>
    </div>
  );
}


